import { useMediaQuery, useTheme } from "@mui/material";

import {
  SHELL_PHONE_BROWSER_MATCH,
  VIEWPORT_2K_MIN_WIDTH_MATCH,
  VIEWPORT_COMPACT_MAX_WIDTH_MATCH,
  VIEWPORT_SHORT_MAX_HEIGHT_MATCH,
} from "config";

/**
 * The viewport tiers the platform lays out against, in one place.
 *
 * Every page used to re-declare `useTheme()` + `useMediaQuery` under its own
 * name (isMobile / isPhone / isNarrow / isXs / isSmDown) against whichever
 * breakpoint the author picked, so the page and the shell disagreed about
 * where "mobile" starts. These are the names to use instead.
 */
export function useResponsive() {
  const theme = useTheme();
  // Without noSsr the first render always answers false, so the heavy desktop
  // tree mounts for a frame before the card layout replaces it.
  const opts = { noSsr: true } as const;
  return {
    /**
     * Cards instead of tables, one pane at a time. Matches the breakpoint at
     * which the shell puts its navigation in a drawer, so a tablet is never
     * left with a desktop page and no sidebar.
     */
    isCardMode: useMediaQuery(theme.breakpoints.down("md"), opts),
    /** True phone width - the tightest layout, inside isCardMode. */
    isPhone: useMediaQuery(theme.breakpoints.down("sm"), opts),
    /** Laptop-class width (1366x768 and friends): desktop layout, tight chrome. */
    isCompact: useMediaQuery(VIEWPORT_COMPACT_MAX_WIDTH_MATCH, opts),
    /** Short viewport: a 768 screen after browser chrome, or a phone on its side. */
    isShort: useMediaQuery(VIEWPORT_SHORT_MAX_HEIGHT_MATCH, opts),
    /** 2K and wider (2560x1440): room for what the narrower tiers cut. */
    is2K: useMediaQuery(VIEWPORT_2K_MIN_WIDTH_MATCH, opts),
    /**
     * A phone in a browser tab rather than a Home Screen app: the shell runs
     * under the browser's bars and keeps its menu inside the shell
     * (config/layout.ts, SHELL_PHONE_BROWSER_MATCH).
     */
    isPhoneBrowser: useMediaQuery(SHELL_PHONE_BROWSER_MATCH, opts),
  };
}

export default useResponsive;
